/**
 * Preferencia del launcher post-login · "mostrar accesos rápidos al entrar".
 *
 * Persiste en localStorage (per-device, default ON). Es una pref de UI: no
 * justifica una tabla en `core.*` (= migración productiva). Si en algún momento
 * se quiere sync cross-device, se migra a una tabla de prefs de usuario.
 *
 * `useSyncExternalStore` mantiene en sync el toggle del launcher y el de
 * Settings dentro de la misma pestaña (localStorage no emite `storage` en la
 * pestaña que escribe → emitimos un evento propio).
 */
import { useCallback, useSyncExternalStore } from 'react';

const KEY = 'core:launcher-enabled';
const EVT = 'core:launcher-pref';

export function isLauncherEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(KEY) !== '0'; // default ON (null → true)
}

function writeLauncherEnabled(enabled: boolean): void {
  try {
    window.localStorage.setItem(KEY, enabled ? '1' : '0');
  } catch {
    // ignore quota / privacy-mode errors
  }
  window.dispatchEvent(new Event(EVT));
}

function subscribe(cb: () => void): () => void {
  window.addEventListener(EVT, cb);
  window.addEventListener('storage', cb);
  return () => {
    window.removeEventListener(EVT, cb);
    window.removeEventListener('storage', cb);
  };
}

/** [enabled, setEnabled] reactivo al cambio en cualquier parte de la pestaña. */
export function useLauncherPref(): [boolean, (enabled: boolean) => void] {
  const enabled = useSyncExternalStore(subscribe, isLauncherEnabled, () => true);
  const setEnabled = useCallback((v: boolean) => writeLauncherEnabled(v), []);
  return [enabled, setEnabled];
}
